/**
 * MediaReaperPanel — iter174i
 *
 * Admin-UI für den Hard-Delete-Reaper (physische Löschung soft-deleted Files
 * nach Ablauf der Karenzzeit).
 *
 * Zwei-Schritte-Flow:
 *   1. Vorschau (dry-run): POST /admin/media/reaper/preview?grace_days=…
 *   2. Nach Bestätigung: POST /admin/media/reaper/execute
 *
 * Zeigt zusätzlich den letzten automatischen Reaper-Lauf aus dem Scheduler
 * (GET /admin/media/reaper/last-run).
 */
import { useEffect, useState, useCallback } from "react";
import { AlertTriangle, Loader2, Trash2, CheckCircle2, Info, X, Download, Mail, Copy } from "lucide-react";
import api from "@/lib/api";

function fmtDate(iso) {
  if (!iso) return "—";
  return String(iso).slice(0, 19).replace("T", " ");
}

export default function MediaReaperPanel({ onClose }) {
  const [graceDays, setGraceDays] = useState(30);
  const [batchLimit, setBatchLimit] = useState(500);
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [executing, setExecuting] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [lastRun, setLastRun] = useState(null);

  const loadLastRun = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/media/reaper/last-run");
      setLastRun(data);
    } catch {
      /* still initialisiert oder Scheduler noch nicht gelaufen */
    }
  }, []);

  useEffect(() => { loadLastRun(); }, [loadLastRun]);

  const runPreview = async () => {
    setPreviewing(true); setError(""); setResult(null); setConfirmed(false);
    try {
      const { data } = await api.post("/admin/media/reaper/preview", null,
        { params: { grace_days: graceDays, batch_limit: batchLimit } });
      setPreview(data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Vorschau fehlgeschlagen");
    } finally { setPreviewing(false); }
  };

  const runExecute = async () => {
    if (!confirmed || !preview || preview.db_purged === 0) return;
    setExecuting(true); setError("");
    try {
      const { data } = await api.post("/admin/media/reaper/execute", null,
        { params: { grace_days: graceDays, batch_limit: batchLimit } });
      setResult(data);
      loadLastRun();
    } catch (e) {
      setError(e?.response?.data?.detail || "Ausführung fehlgeschlagen");
    } finally { setExecuting(false); }
  };

  return (
    <div
      data-testid="media-reaper-dialog"
      className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
    >
      <div className="bg-white w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-lg">
        <div className="p-4 border-b border-[#E5E5E5] flex items-center justify-between">
          <h3 className="text-sm font-bold flex items-center gap-2">
            <Trash2 size={14} /> Hard-Delete-Reaper (endgültige Löschung)
          </h3>
          <button onClick={onClose} data-testid="media-reaper-close" className="p-1 hover:bg-[#F5F5F5]">
            <X size={16} />
          </button>
        </div>

        <div className="p-4 space-y-3">
          <div className="p-3 bg-amber-50 border border-amber-200 text-[11px] text-amber-800 flex items-start gap-2">
            <AlertTriangle size={14} className="mt-0.5 shrink-0" />
            <div>
              <b>Achtung:</b> Der Reaper löscht Dateien <b>physisch</b> aus Object-Storage UND DB.
              Nur Files, die seit <b>{graceDays} Tagen</b> soft-deleted sind UND weder im
              file_usage-Index noch im Fallback-Scan referenziert werden, sind betroffen.
              Referenzierte Files werden automatisch <i>wiederhergestellt</i>.
            </div>
          </div>

          {lastRun && lastRun.last_run_at && (
            <div className="p-2 bg-[#F5F5F5] border border-[#E5E5E5] text-[11px] flex items-start gap-2"
                 data-testid="media-reaper-last-run">
              <Info size={12} className="mt-0.5 shrink-0 text-[#666]" />
              <div className="flex-1">
                <b>Letzter automatischer Lauf:</b> {fmtDate(lastRun.last_run_at)}
                {lastRun.last_result && (
                  <span className="ml-2 text-[#666]">
                    (gelöscht: {lastRun.last_result.db_purged ?? 0} ·
                    restauriert: {lastRun.last_result.restored ?? 0} ·
                    Dead-Letter: {lastRun.last_result.dead_lettered ?? 0} ·
                    Storage-Retry: {lastRun.last_result.storage_failed ?? 0})
                  </span>
                )}
              </div>
            </div>
          )}

          <div className="flex flex-wrap items-end gap-3">
            <label className="text-xs">
              Karenzzeit
              <input
                type="number" min="1" max="365" value={graceDays}
                onChange={(e) => setGraceDays(parseInt(e.target.value, 10) || 30)}
                data-testid="media-reaper-grace-days"
                className="ml-2 h-8 w-20 border border-[#DDD] px-2 text-sm"
              />
              <span className="ml-1 text-[11px] text-[#666]">Tage</span>
            </label>
            <label className="text-xs">
              Batch-Limit
              <input
                type="number" min="1" max="5000" value={batchLimit}
                onChange={(e) => setBatchLimit(parseInt(e.target.value, 10) || 500)}
                data-testid="media-reaper-batch-limit"
                className="ml-2 h-8 w-24 border border-[#DDD] px-2 text-sm"
              />
            </label>
            <button
              data-testid="media-reaper-preview-btn"
              onClick={runPreview}
              disabled={previewing}
              className="h-8 px-3 text-xs font-semibold border border-[#0A0A0A] hover:bg-[#F5F5F5] flex items-center gap-1.5"
            >
              {previewing ? <Loader2 size={12} className="animate-spin" /> : null}
              Vorschau starten
            </button>
          </div>

          {error && (
            <div className="p-2 bg-red-50 border border-red-200 text-xs text-red-700 flex items-center gap-1.5">
              <AlertTriangle size={12} /> {String(error)}
            </div>
          )}

          {preview && !result && (
            <div className="space-y-2" data-testid="media-reaper-preview-result">
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div className="p-2 bg-[#FAFAFA] border border-[#E5E5E5]">
                  <p className="text-[10px] text-[#999] uppercase">Kandidaten geprüft</p>
                  <p className="text-lg font-bold">{preview.candidates_scanned}</p>
                </div>
                <div className="p-2 bg-[#FAFAFA] border border-[#E5E5E5]">
                  <p className="text-[10px] text-[#999] uppercase">Würde gelöscht</p>
                  <p className="text-lg font-bold text-red-600" data-testid="media-reaper-preview-count">
                    {preview.db_purged}
                  </p>
                </div>
                <div className="p-2 bg-[#FAFAFA] border border-[#E5E5E5]">
                  <p className="text-[10px] text-[#999] uppercase">Würde restauriert</p>
                  <p className="text-lg font-bold text-emerald-600">{preview.restored}</p>
                </div>
                <div className="p-2 bg-[#FAFAFA] border border-[#E5E5E5]">
                  <p className="text-[10px] text-[#999] uppercase">Gesamt-Backlog</p>
                  <p className="text-lg font-bold">{preview.candidates_total}</p>
                </div>
              </div>

              {preview.sample_deleted && preview.sample_deleted.length > 0 && (
                <details className="border border-[#E5E5E5] text-xs">
                  <summary className="p-2 cursor-pointer bg-[#F5F5F5] font-semibold">
                    Beispiel-Pfade (max. 20)
                  </summary>
                  <div className="p-2 max-h-40 overflow-y-auto font-mono text-[10px] space-y-0.5">
                    {preview.sample_deleted.map((sp, i) => <div key={i}>{sp}</div>)}
                  </div>
                </details>
              )}

              {preview.db_purged > 0 && (
                <>
                  <label className="flex items-start gap-2 text-xs pt-2">
                    <input
                      type="checkbox"
                      data-testid="media-reaper-confirm"
                      checked={confirmed}
                      onChange={(e) => setConfirmed(e.target.checked)}
                    />
                    <span>Ich bestätige, dass die <b>{preview.db_purged}</b> Dateien
                      physisch und endgültig aus Storage + DB gelöscht werden sollen.</span>
                  </label>
                  <button
                    data-testid="media-reaper-execute-btn"
                    onClick={runExecute}
                    disabled={!confirmed || executing}
                    className="h-9 px-4 text-xs font-semibold bg-red-600 text-white hover:bg-red-700 disabled:opacity-40 flex items-center gap-2"
                  >
                    {executing ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />}
                    {preview.db_purged} Dateien endgültig löschen
                  </button>
                </>
              )}
            </div>
          )}

          {result && (
            <div className="p-3 bg-emerald-50 border border-emerald-200 text-xs"
                 data-testid="media-reaper-result">
              <p className="flex items-center gap-1.5 font-semibold text-emerald-800">
                <CheckCircle2 size={14} /> Reaper abgeschlossen
              </p>
              <div className="mt-1 grid grid-cols-2 gap-1 text-emerald-700">
                <p>Gelöscht: <b>{result.db_purged}</b></p>
                <p>Storage-Objekte: <b>{result.storage_deleted}</b></p>
                <p>Restauriert: <b>{result.restored}</b></p>
                <p>Dead-Letter: <b>{result.dead_lettered ?? 0}</b></p>
              </div>
              {result.errors && result.errors.length > 0 && (
                <details className="mt-2">
                  <summary className="cursor-pointer text-[10px] text-emerald-800">
                    {result.errors.length} Fehler (Details)
                  </summary>
                  <div className="mt-1 max-h-24 overflow-y-auto text-[10px] text-emerald-900 font-mono">
                    {result.errors.map((e, i) => <div key={i}>· {e}</div>)}
                  </div>
                </details>
              )}
            </div>
          )}

          <StorageCleanupReportSection />
        </div>
      </div>
    </div>
  );
}

/**
 * StorageCleanupReportSection — iter174j
 * Zeigt Dead-Letter/Retry-Queue-Bestand + Buttons zum CSV-Download +
 * Anzeige der Support-E-Mail-Vorlage (mit Copy-to-Clipboard).
 */
function StorageCleanupReportSection() {
  const [emailData, setEmailData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [ackBusy, setAckBusy] = useState(false);
  const [ackResult, setAckResult] = useState(null);

  const loadEmail = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/media/reaper/storage-cleanup-email-template");
      setEmailData(data);
    } catch {
      /* silent */
    } finally { setLoading(false); }
  };

  const downloadCsv = async () => {
    try {
      const resp = await api.get("/admin/media/reaper/storage-cleanup-report.csv",
                                  { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([resp.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `stilltouched-storage-cleanup-${Date.now()}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      /* silent */
    }
  };

  const copyEmail = async () => {
    if (!emailData) return;
    const text = `An: ${emailData.to}\nBetreff: ${emailData.subject}\n\n${emailData.body}`;
    try { await navigator.clipboard.writeText(text); setCopied(true);
      setTimeout(() => setCopied(false), 2500); } catch { /* silent */ }
  };

  const ackAll = async () => {
    if (!window.confirm("Alle zombified Objekte als 'von Emergent bulk-gelöscht' markieren? Sie verschwinden dann aus Report + Counter.")) return;
    setAckBusy(true);
    try {
      const { data } = await api.post("/admin/media/reaper/zombified/ack",
                                        { all: true });
      setAckResult(data);
      loadEmail();
    } finally { setAckBusy(false); }
  };

  return (
    <div className="pt-4 mt-4 border-t border-[#E5E5E5]" data-testid="storage-cleanup-report-section">
      <p className="text-xs font-bold mb-2">Emergent-Storage-Cleanup-Report</p>
      <p className="text-[11px] text-[#666] mb-3">
        Emergent-Object-Storage hat kein DELETE-Verb (Allow: PUT, GET, HEAD).
        Der Reaper überschreibt 405-Objekte via Empty-PUT (Storage-Bytes werden frei).
        Für vollständige Bucket-Bereinigung: CSV-Report an Emergent-Support schicken.
      </p>
      <div className="flex flex-wrap gap-2">
        <button
          data-testid="storage-report-csv-btn"
          onClick={downloadCsv}
          className="h-8 px-3 text-xs font-semibold border border-[#0A0A0A] hover:bg-[#F5F5F5] flex items-center gap-1.5"
        >
          <Download size={12} /> CSV-Report laden
        </button>
        <button
          data-testid="storage-report-email-btn"
          onClick={loadEmail}
          disabled={loading}
          className="h-8 px-3 text-xs font-semibold border border-[#0A0A0A] hover:bg-[#F5F5F5] flex items-center gap-1.5"
        >
          {loading ? <Loader2 size={12} className="animate-spin" /> : <Mail size={12} />}
          Support-E-Mail-Vorlage anzeigen
        </button>
        <button
          data-testid="storage-report-ack-all-btn"
          onClick={ackAll}
          disabled={ackBusy}
          className="h-8 px-3 text-xs font-semibold border border-emerald-700 text-emerald-700 hover:bg-emerald-50 flex items-center gap-1.5"
          title="Nach Bulk-Delete durch Emergent-Support alle Zombified-Rows abhaken"
        >
          {ackBusy ? <Loader2 size={12} className="animate-spin" /> : <CheckCircle2 size={12} />}
          Alle als bulk-gelöscht bestätigen
        </button>
      </div>

      {ackResult && (
        <div className="mt-2 p-2 bg-emerald-50 border border-emerald-200 text-[11px]"
             data-testid="storage-report-ack-result">
          {ackResult.acked} Zombified-Rows als acked markiert.
        </div>
      )}

      {emailData && (
        <div className="mt-3 p-3 bg-[#FAFAFA] border border-[#E5E5E5] text-xs"
             data-testid="storage-report-email-preview">
          <div className="flex items-center justify-between mb-2">
            <p className="text-[11px] text-[#666]">
              An: <b>{emailData.to}</b> · Betreff: <b>{emailData.subject}</b>
            </p>
            <button
              data-testid="storage-report-email-copy"
              onClick={copyEmail}
              className="text-[11px] px-2 py-1 border border-[#DDD] hover:bg-white flex items-center gap-1"
            >
              <Copy size={10} /> {copied ? "Kopiert!" : "In Zwischenablage"}
            </button>
          </div>
          <pre className="text-[10.5px] whitespace-pre-wrap font-mono max-h-64 overflow-y-auto">
            {emailData.body}
          </pre>
          <p className="mt-1 text-[10px] text-[#999]">
            Dead-Letter: {emailData.dead_letter_total} · Retry-Queue: {emailData.retry_queue_total}
          </p>
        </div>
      )}
    </div>
  );
}
